<template>
  <div class="container-fluid py-4">
    <!-- Header -->
        <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
              <h6 class="text-white text-capitalize ps-3 mb-0">لوحة التحكم</h6>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">جار التحميل...</span>
            </div>
            <p class="mt-3">جار تحميل البيانات...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <h5 class="text-danger">خطأ في تحميل البيانات</h5>
            <p class="text-muted">{{ error }}</p>
            <button @click="loadStats" class="btn btn-primary">إعادة المحاولة</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Stats -->
    <div v-else-if="stats" class="row">
      <!-- Admin Stats (Hide for Teachers if teacher-specific stats are present) -->
      <template v-if="!isTeacher">
        <!-- Teachers Card -->
        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <div class="card">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-primary shadow-primary text-center border-radius-xl mt-n4 position-absolute">
                <i class="material-icons opacity-10" aria-hidden="true">person</i>
              </div>
              <div class="text-end pt-1">
                <p class="text-sm mb-0 text-capitalize">المعلمين</p>
                <h4 class="mb-0">{{ stats.teachers_total || 0 }}</h4>
              </div>
            </div>
            <hr class="dark horizontal my-0">
            <div class="card-footer p-3">
              <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.teachers_total || 0 }}</span> إجمالي المعلمين</p>
            </div>
          </div>
        </div>

        <!-- Students Card -->
        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <div class="card">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 position-absolute">
                <i class="material-icons opacity-10" aria-hidden="true">school</i>
              </div>
              <div class="text-end pt-1">
                <p class="text-sm mb-0 text-capitalize">الطلاب</p>
                <h4 class="mb-0">{{ stats.students_total || 0 }}</h4>
              </div>
            </div>
            <hr class="dark horizontal my-0">
            <div class="card-footer p-3">
              <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.students_total || 0 }}</span> إجمالي الطلاب</p>
            </div>
          </div>
        </div>

        <!-- Courses Card -->
        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <div class="card">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 position-absolute">
                <i class="material-icons opacity-10" aria-hidden="true">book</i>
              </div>
              <div class="text-end pt-1">
                <p class="text-sm mb-0 text-capitalize">الدروس</p>
                <h4 class="mb-0">{{ stats.courses_total || 0 }}</h4>
              </div>
            </div>
            <hr class="dark horizontal my-0">
            <div class="card-footer p-3">
              <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.courses_total || 0 }}</span> إجمالي الدروس</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Teacher Financial Stats -->
      <template v-else>
        <div class="col-xl-6 col-sm-6 mb-xl-0 mb-4">
          <div class="card">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 position-absolute">
                <i class="material-icons opacity-10" aria-hidden="true">account_balance_wallet</i>
              </div>
              <div class="text-end pt-1">
                <p class="text-sm mb-0 text-capitalize font-weight-bold">الرصيد الصافي المستحق</p>
                <h4 class="mb-0">{{ formatCurrency(stats.financial_summary?.net_balance) }}</h4>
              </div>
            </div>
            <hr class="dark horizontal my-0">
            <div class="card-footer p-3">
              <p class="mb-0" v-if="stats.financial_summary?.net_balance > 0">رصيد متاح للصرف حالياً</p>
              <p class="mb-0 text-muted" v-else>لا توجد مستحقات مالية حالية</p>
            </div>
          </div>
        </div>

        <div class="col-xl-6 col-sm-6 mb-xl-0 mb-4">
          <div class="card">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 position-absolute">
                <i class="material-icons opacity-10" aria-hidden="true">payments</i>
              </div>
              <div class="text-end pt-1">
                <p class="text-sm mb-0 text-capitalize font-weight-bold">اجمالي مدفوع للمدرس</p>
                <h4 class="mb-0">{{ formatCurrency(stats.financial_summary?.total_earned) }}</h4>
              </div>
            </div>
            <hr class="dark horizontal my-0">
            <div class="card-footer p-3">
              <p class="mb-0">إجمالي ما تم تحقيقه منذ البداية</p>
            </div>
          </div>
        </div>

        <!-- Zero State for Courses -->
        <div class="col-12 mt-4" v-if="!stats.courses?.length">
          <div class="card p-4 text-center">
            <i class="material-icons text-muted scale-2 mb-3">history_toggle_off</i>
            <h6 class="text-muted">لا توجد معاملات مالية أو دروس مسجلة حالياً.</h6>
          </div>
        </div>
        
        <!-- Course Statistics Table for Teachers -->
        <div class="col-12 mt-4" v-else>
          <div class="card shadow-sm">
            <div class="card-header pb-0 p-3">
              <h6 class="mb-0">إحصائيات الكورسات والحصص</h6>
            </div>
            <div class="card-body p-3">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-right">الكورس / المرحلة</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-right">الحصص (منفذة / ملغاة)</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-right">سعر الحصة</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-right">إجمالي الإيرادات</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="course in stats.courses" :key="course.id">
                      <td class="text-right">
                        <div class="d-flex flex-column justify-content-center ps-3">
                          <h6 class="mb-0 text-sm">{{ course.title_ar }}</h6>
                          <p class="text-xs text-secondary mb-0">{{ course.grade_name || (course.grade && course.grade.name_ar) }}</p>
                        </div>
                      </td>
                      <td class="text-right">
                        <span class="badge badge-sm bg-gradient-success me-1">{{ course.done_sessions_count || 0 }} منفذة</span>
                        <span class="badge badge-sm bg-gradient-danger">{{ course.cancelled_sessions_count || 0 }} ملغاة</span>
                      </td>
                      <td class="text-right">
                        <p class="text-sm font-weight-bold mb-0 text-primary">{{ formatCurrency(course.session_rate || course.price_per_session || 0) }}</p>
                      </td>
                      <td class="text-right">
                        <p class="text-sm font-weight-bold mb-0 text-success">{{ formatCurrency(course.total_revenue || 0) }}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>

    </div>

  </div>
</template>

<script>
import StatsService from '@/services/stats.service';
import permissionsMixin from '@/mixins/permissions';

export default {
  name: 'Dashboard',
  mixins: [permissionsMixin],
  data() {
    return {
      loading: true,
      error: null,
      stats: null,
      statsService: StatsService,
      pollingInterval: null
    };
  },
  computed: {
    canViewStats() {
      return this.isSuperAdmin() || this.checkAnyPermission([
        'view stats', 'view-stats', 'view dashboard', 'view-dashboard'
      ]);
    },
    isTeacher() {
      const user = this.$store.getters['auth/currentUser'];
      return user?.roles?.some(r => r.name === 'teacher') || user?.type === 'teacher';
    }
  },
  async created() {
    await this.loadStats();
    if (this.isTeacher) {
      this.startPolling();
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  mounted() {
    const token = this.$route.query.token;
    if (token) {
      // Store the token in localStorage
      localStorage.setItem('jwt_token', token);

      // Remove the token from the URL for security and cleanliness
      this.$router.replace({ query: null });

      // You can now use the token for authenticated API requests
      // For example, you might want to fetch the user's profile or dispatch a Vuex action
      // axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // this.$store.dispatch('fetchUser');
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null || value === 0 || isNaN(value)) {
        return "0.00 ج.م";
      }
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency: 'EGP',
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await this.statsService.getTeacherDashboardStats();
          if (response.data) {
            this.stats = { ...this.stats, ...response.data };
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 30000); // Poll every 30 seconds
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async loadStats() {
      try {
        this.loading = true;
        this.error = null;
        
        // Check if user has permission to view stats
        if (!this.canViewStats) {
          console.log('User does not have permission to view stats, redirecting...');
          this.redirectToFirstAvailablePage();
          return;
        }
        
        let response;
        if (this.isTeacher) {
          response = await this.statsService.getTeacherDashboardStats();
        } else {
          response = await this.statsService.getOverview();
        }

        console.log('Stats response:', response);
        
        if (response.data) {
          this.stats = response.data;
        } else {
          throw new Error('No stats data received');
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        
        // Check if it's a 403 Forbidden error (permission denied)
        if (error.response?.status === 403) {
          console.log('403 Forbidden - User does not have view stats permission');
          // Redirect to first available page based on user permissions
          this.redirectToFirstAvailablePage();
          return;
        }
        
        this.error = error.message || 'فشل في تحميل البيانات';
      } finally {
        this.loading = false;
      }
    },

    getStatusLabel(status) {
      const labels = {
        awaiting_payment: 'في انتظار الدفع',
        paid: 'مدفوعة',
        cancelled: 'ملغاة'
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        awaiting_payment: 'bg-warning',
        paid: 'bg-success',
        cancelled: 'bg-danger'
      };
      return classes[status] || 'bg-light';
    },


    redirectToFirstAvailablePage() {
      const user = this.$store.getters['auth/currentUser'];
      const permissions = user?.permissions || [];
      
      const permissionRoutes = [
        { permissions: ['view subjects', 'view-subjects', 'create subjects', 'create-subjects', 'edit subjects', 'edit-subjects'], route: 'Subjects' },
        { permissions: ['view courses', 'view-courses', 'create courses', 'create-courses', 'edit courses', 'edit-courses'], route: 'Courses' },
        { permissions: ['view teachers', 'view-teachers', 'create teachers', 'create-teachers', 'edit teachers', 'edit-teachers'], route: 'Teachers' },
        { permissions: ['view questions', 'view-questions', 'create questions', 'create-questions', 'edit questions', 'edit-questions', 'view question options', 'view-question-options', 'create question options', 'create-question-options', 'edit question options', 'edit-question-options', 'view answers', 'view-answers', 'create answers', 'create-answers', 'edit answers', 'edit-answers', 'view homework', 'view-homework', 'create homework', 'create-homework', 'edit homework', 'edit-homework'], route: 'SessionQuestionsList' },
        { permissions: ['view reservations', 'view-reservations', 'create reservations', 'create-reservations', 'edit reservations', 'edit-reservations'], route: 'ReservationsIndex' },
        { permissions: ['view home banners', 'view-home-banners', 'create home banners', 'create-home-banners', 'edit home banners', 'edit-home-banners'], route: 'Home Banners' },
        { permissions: ['view countries', 'view-countries', 'create countries', 'create-countries', 'edit countries', 'edit-countries'], route: 'Countries' }
      ];

      for (const item of permissionRoutes) {
        if (permissions.some(permission => item.permissions.includes(permission))) {
          this.$router.push({ name: item.route });
          return;
        }
      }

      // Fallback to no-access page if no specific permissions found
      this.$router.push({ name: 'NoAccess' });
    }
  }
};
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #0277bd 0%, #29b6f6 100%);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f57c00 0%, #ffb74d 100%);
}

.shadow-primary {
  box-shadow: 0 4px 20px 0 rgba(13, 71, 161, 0.14);
}

.shadow-success {
  box-shadow: 0 4px 20px 0 rgba(46, 125, 50, 0.14);
}

.shadow-info {
  box-shadow: 0 4px 20px 0 rgba(2, 119, 189, 0.14);
}

.shadow-warning {
  box-shadow: 0 4px 20px 0 rgba(245, 124, 0, 0.14);
}

.icon {
  width: 3rem;
  height: 3rem;
}

.icon-lg {
  width: 4rem;
  height: 4rem;
}

.border-radius-xl {
  border-radius: 0.75rem;
}

.text-xxs {
  font-size: 0.75rem;
}

.card {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 0;
}

.card:hover {
  box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}
</style>
